import { useEffect } from 'react';
import { InAppNotification } from '../../api/Types';
import { inAppNotificationService } from '../../services/InAppNotificationService';
import { createRealtimeNotification } from './inAppNotifications.helpers';

interface UseInAppNotificationRealtimeOptions {
  authorId?: number;
  loadInAppNotifications: () => Promise<void>;
  onUnreadNotification: (notification: InAppNotification) => void;
}

export const useInAppNotificationRealtime = ({
  authorId,
  loadInAppNotifications,
  onUnreadNotification,
}: UseInAppNotificationRealtimeOptions): void => {
  useEffect(() => {
    if (!authorId) {
      return;
    }

    let isCancelled = false;
    let cleanup: (() => void) | null = null;

    const subscribeToRealtimeNotifications = async () => {
      try {
        const realtimeClient = await inAppNotificationService.createRealtimeClient();
        if (isCancelled) {
          return;
        }

        const channel = realtimeClient
          .channel(`in_app_notifications_${authorId}`)
          .on(
            'postgres_changes',
            {
              event: 'INSERT',
              schema: 'public',
              table: 'in_app_notifications',
              filter: `recipient_id=eq.${authorId}`,
            },
            (payload) => {
              const incoming = createRealtimeNotification(payload.new as any);

              if (!incoming.is_read) {
                onUnreadNotification(incoming);
              }
            }
          )
          .on(
            'postgres_changes',
            {
              event: 'UPDATE',
              schema: 'public',
              table: 'in_app_notifications',
              filter: `recipient_id=eq.${authorId}`,
            },
            () => {
              void loadInAppNotifications();
            }
          )
          .subscribe();

        cleanup = () => {
          void realtimeClient.removeChannel(channel);
        };
      } catch (error) {
        console.error('Failed to subscribe to in-app notifications:', error);
      }
    };

    void subscribeToRealtimeNotifications();

    return () => {
      isCancelled = true;
      if (cleanup) {
        cleanup();
      }
    };
  }, [authorId, loadInAppNotifications, onUnreadNotification]);
};

export default useInAppNotificationRealtime;
